import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip, Skeleton } from '@mui/material';
import { getGamePhrases, PhraseList } from '../../services/contents_api';
import { styled } from '@mui/material/styles';
import PhraseUploadButton from './PhraseUploadButton';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useNavigate } from 'react-router-dom';

interface GamePhraseSectionProps {
  gameId: number;
}

const PhraseCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.01)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    transform: 'none', // ホバー時の浮き上がりアニメーションを無効化
  }
}));

const SourceChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '0.7rem',
  height: '24px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
}));

const GamePhraseSection: React.FC<GamePhraseSectionProps> = ({ gameId }) => {
  const [phrases, setPhrases] = useState<PhraseList[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPhrases = async () => {
    try {
      const response = await getGamePhrases(gameId);
      setPhrases(response);
    } catch (error) {
      console.error('キャッチコピーの取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhrases();
  }, [gameId]);

  const handlePhraseClick = (phraseId: number) => {
    navigate(`/phrase/${phraseId}`);
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h2" gutterBottom sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>キャッチコピー</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      </Box>
    );
  }

  if (phrases.length === 0) {
    return (
      <Box sx={{ mt: { xs: 3, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 1 
        }}>
          <Typography variant="h2" gutterBottom sx={{ mb: 0, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>キャッチコピー</Typography>
          <PhraseUploadButton 
            gameId={gameId}
            onUploadComplete={fetchPhrases}
            onError={(error) => console.error(error)}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          このゲームのキャッチコピーはまだ登録されていません。
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: { xs: 3, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 1 
      }}>
        <Typography variant="h2" gutterBottom sx={{ mb: 0, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>キャッチコピー</Typography>
        <PhraseUploadButton 
          gameId={gameId}
          onUploadComplete={fetchPhrases}
          onError={(error) => console.error(error)}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {phrases.map((phrase) => (
          <PhraseCard 
            key={phrase.id}
            onClick={() => handlePhraseClick(phrase.id)}
            sx={{ cursor: 'pointer' }}
          >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <FormatQuoteIcon sx={{ transform: 'scale(-1, -1)', color: 'text.secondary', fontSize: '1.2rem' }} />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 500,
                      lineHeight: 1.4,
                      pr: phrase.source ? 8 : 0,
                      fontSize: '0.875rem'
                    }}
                  >
                    {phrase.content}
                  </Typography>
                </Box>
                {phrase.context && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      mt: 1,
                      fontSize: '0.75rem',
                      pl: '28px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.4,
                      color: 'rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    {phrase.context}
                  </Typography>
                )}
              </CardContent>
              {phrase.source && (
                <SourceChip 
                  label={phrase.source} 
                  size="small"
                />
              )}
            </PhraseCard>
        ))}
      </Box>
    </Box>
  );
};

export default GamePhraseSection; 